/**
 * Finestra di 24 ore aggiornata dal vivo sui numeri "Ufficiale".
 *
 * Perche' esiste (26/09/2026): lo stato della finestra si ricaricava solo
 * cambiando chat. Se il cliente scriveva mentre la chat era aperta con la
 * finestra chiusa, il CRM continuava a proporre solo i modelli.
 *
 * Un messaggio del cliente arrivato all'istante T apre (o allunga) la finestra
 * fino a T + 24 ore: lo si applica subito, senza aspettare la tabella
 * whatsapp_csw_state (che il webhook aggiorna subito DOPO aver salvato il
 * messaggio, quindi rileggerla nello stesso istante darebbe il valore vecchio).
 *
 * Modulo puro, testato in __tests__/cswLive.test.ts.
 */

import type { CSWState } from './types';
import { computeCSWState } from './csw';
import { e164FromAny } from './identity';

const DAY_MS = 24 * 3600 * 1000;

interface MessageLike {
  chatId: string;
  direction: string;
  timestamp: Date | string | number;
}

/** Istante (ms) dell'ultimo messaggio del cliente nella chat, oppure null. */
export function latestInboundAt(messages: MessageLike[], chatId: string | null): number | null {
  const target = e164FromAny(chatId);
  if (!target) return null;
  let latest: number | null = null;
  for (const m of messages) {
    if (m.direction !== 'inbound' || e164FromAny(m.chatId) !== target) continue;
    const t = new Date(m.timestamp).getTime();
    if (Number.isFinite(t) && (latest === null || t > latest)) latest = t;
  }
  return latest;
}

/** Stato della finestra tenendo conto di un messaggio del cliente arrivato a `inboundAtMs`. */
export function cswWithInbound(state: CSWState | null, inboundAtMs: number | null, nowMs: number = Date.now()): CSWState | null {
  if (inboundAtMs === null) return state;
  const expiry = inboundAtMs + DAY_MS;
  if (state?.cswExpiresAt && state.cswExpiresAt.getTime() >= expiry) return state;
  return computeCSWState(new Date(expiry).toISOString(), state?.fepExpiresAt ? state.fepExpiresAt.toISOString() : null, nowMs);
}

/** Ricalcola aperta/chiusa e minuti rimasti all'istante `nowMs` (la finestra scade anche a chat aperta). */
export function cswAt(state: CSWState | null, nowMs: number): CSWState | null {
  if (!state) return null;
  return computeCSWState(
    state.cswExpiresAt ? state.cswExpiresAt.toISOString() : null,
    state.fepExpiresAt ? state.fepExpiresAt.toISOString() : null,
    nowMs,
  );
}

export interface CswViewInput {
  isCloud: boolean;
  chatId: string | null;
  /** Chat a cui si riferisce l'ultima lettura arrivata (null = nessuna lettura ancora). */
  loadedFor: string | null;
  dbState: CSWState | null;
  lastInbound: number | null;
  /** Istante in cui Meta ha respinto un invio per finestra chiusa in questa chat (o null). */
  forcedClosedAt: number | null;
  nowMs: number;
}

export interface CswView {
  cswState: CSWState | null;
  /** True finche' la lettura della finestra per questa chat non e' arrivata. */
  unknown: boolean;
  canSendFreeForm: boolean;
}

/**
 * Stato della finestra da mostrare nel composer.
 * - Numero Standard: nessun vincolo.
 * - Lettura non ancora arrivata per QUESTA chat: sconosciuto, testo libero bloccato.
 * - Meta ha respinto un invio per finestra chiusa: chiusa, finche' il cliente non riscrive.
 */
export function resolveCswView(i: CswViewInput): CswView {
  if (!i.isCloud || !i.chatId) return { cswState: null, unknown: false, canSendFreeForm: true };
  if (i.loadedFor !== i.chatId) return { cswState: null, unknown: true, canSendFreeForm: false };
  let state = cswAt(cswWithInbound(i.dbState, i.lastInbound, i.nowMs), i.nowMs);
  const forced = i.forcedClosedAt !== null && (i.lastInbound === null || i.lastInbound < i.forcedClosedAt);
  if (forced) {
    state = {
      cswExpiresAt: state?.cswExpiresAt && state.cswExpiresAt.getTime() <= i.nowMs ? state.cswExpiresAt : new Date(i.forcedClosedAt as number),
      fepExpiresAt: null,
      isOpen: false,
      minutesRemaining: null,
    };
  }
  // Lettura fallita (state null): non si blocca, Meta rispondera' con l'errore giusto.
  return { cswState: state, unknown: false, canSendFreeForm: !state || state.isOpen };
}
